import { LaunchpadTaskSchema } from './interfaces';

import { OnboardingTaskTemplateName } from 'modules/HubV2/OnboardingV2/constants';
import { BusinessVerificationTaskName } from 'modules/Onboarding/BusinessVerificationFlow/constants';
import states from 'modules/common/constants/states';
import { getProcessingTimePhrase } from 'modules/common/constants/stateFormationTimes';
import UserType from 'modules/common/types/UserType.enum';
import {
    LaunchpadFacadeSubtask,
    LaunchpadSubtaskType,
    TaskStatus,
} from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';

// Task IDs
export const S_CORP_ELECTION_UPLOAD_TASK = 's_corp_election_upload';
export const INSURANCE_SETUP_ID = 'insurance_setup';
export const COMPLETE_APPLICATION_ID = 'complete_application';
export const REGISTER_YOUR_LLC_ID = 'register_your_llc';
export const BUSINESS_VERIFICATION_COMPLETE_ID = 'business_verification_complete';

// Helper functions
const getStateFullName = (stateAbbr?: string): string => {
    return states[stateAbbr || ''] || stateAbbr || 'state';
};

// Subtask definitions
const VERIFY_SCORP_APPROVAL_LETTER_SUBTASK: LaunchpadFacadeSubtask = {
    title: 'Verify S Corp Approval Letter',
    description:
        "Collective will verify your documents within two business days. We'll notify you via email when we're done.",
    visual_dependencies: [OnboardingTaskTemplateName.VERIFY_SCORP_APPROVAL_LETTER],
};

const BUSINESS_VERIFICATION_UPLOAD_S_CORP_CONFIRMATION_LETTER_SUBTASK = {
    id: BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_S_CORP_CONFIRMATION_LETTER,
    title: 'Upload your S Corp Confirmation Letter (Form CP261)',
    description: 'Submit and confirm a letter from the IRS confirming your S Corp election.',
};

const BUSINESS_VERIFICATION_VERIFY_BUSINESS_SUBTASK: LaunchpadFacadeSubtask = {
    title: 'Verify Business',
    description:
        "Collective will verify your documents within two business days. We'll notify you via email when we're done.",
    visual_dependencies: [BusinessVerificationTaskName.VERIFY_BUSINESS],
};

// Schema factory functions
const getRegisterLLCSchema = (businessAddressState?: string): LaunchpadTaskSchema => ({
    id: REGISTER_YOUR_LLC_ID,
    dependency: [],
    subtasks: [
        {
            id: COMPLETE_APPLICATION_ID,
            title: 'Complete your application',
            description: 'We will use this information to create your LLC.',
            visual_dependencies: [],
        },
        {
            title: 'Collective is submitting your application',
            description: `Collective is sending your application to ${getStateFullName(
                businessAddressState
            )}'s Secretary of State to form your business. The time for the state to process your application generally takes ${getProcessingTimePhrase(
                businessAddressState
            )} but can vary. After that, we will obtain an EIN for you from the IRS. We'll send you an email as soon as we hear back.`,
            visual_dependencies: ['file_llc'],
            // The vendor parks the filing in ready/waiting while it works it, and this is the only
            // step meant to stay live across that window.
            vendorActiveStatuses: [TaskStatus.IN_PROGRESS, TaskStatus.READY, TaskStatus.WAITING],
        },
        {
            title: 'Your LLC has been formed and your EIN has been obtained',
            description:
                'Collective has uploaded your important business documents (such as your Articles of Organization and EIN confirmation letter) have been added to your Documents tab.',
            visual_dependencies: ['file_ein', 'confirm_llc'],
        },
    ],
    title: 'Register your LLC',
    description: 'Create your LLC to get started.',
});

const getPublishBusinessSchema = (businessAddressState?: string): LaunchpadTaskSchema => ({
    id: 'publish_business',
    dependency: ['register_your_llc'],
    subtasks: [],
    title: `Fulfill ${getStateFullName(businessAddressState)}-specific requirements`,
    description:
        'Your state has additional requirements for forming your business. Our partner RASI can do this on your behalf.',
});

const BUSINESS_VERIFICATION_MIDDESK_SUBTASK: LaunchpadSubtaskType = {
    id: BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_ARTICLES_OF_ORGANIZATION_MIDDESK,
    title: 'Upload your Articles of Organization',
    shouldReplaceInfo: true,
    description:
        'Submit and Confirm your Articles of Organization. Collective will try to pull this for you automatically from the Secretary of State. If you do not see a copy of your articles in the next screen, please upload your own copy of the Articles of Organization.',
};

const BUSINESS_VERIFICATION_UPLOAD_EIN_SUBTASK: LaunchpadSubtaskType = {
    id: BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_EIN,
    title: 'Upload confirmation of your EIN',
    description: 'Submit and confirm a letter from the IRS confirming your EIN.',
};

const BUSINESS_VERIFICATION_VERIFY_EIN_CONFIRMATION_LETTER_SUBTASK: LaunchpadFacadeSubtask = {
    title: 'Verify EIN Confirmation Letter',
    description:
        "Collective will verify your documents within two business days. We'll notify you via email when we're done.",
    visual_dependencies: [OnboardingTaskTemplateName.VERIFY_EIN_CONFIRMATION_LETTER],
};

// Static schema definitions
const BUSINESS_VERIFICATION_SCHEMA: LaunchpadTaskSchema = {
    id: BUSINESS_VERIFICATION_COMPLETE_ID,
    dependency: [],
    subtasks: [
        BUSINESS_VERIFICATION_UPLOAD_EIN_SUBTASK,
        BUSINESS_VERIFICATION_MIDDESK_SUBTASK,
        BUSINESS_VERIFICATION_VERIFY_EIN_CONFIRMATION_LETTER_SUBTASK,
        BUSINESS_VERIFICATION_VERIFY_BUSINESS_SUBTASK,
    ],
    title: 'Complete your business verification',
    description: 'Provide a few documents to complete your business verification.',
};

export const OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS: LaunchpadTaskSchema = {
    id: 'open_connect_business_bank_account',
    dependency: [BUSINESS_VERIFICATION_COMPLETE_ID],
    subtasks: [],
    title: 'Open and/or connect new business bank account',
    description: 'Connecting a business bank account is required for accurate accounting and tax compliance.',
};

export const COMPLETE_ACCOUNTING_SETUP: LaunchpadTaskSchema = {
    id: 'complete_accounting_setup',
    dependency: ['open_connect_business_bank_account'],
    subtasks: [],
    title: 'Set up your accounting',
    description: 'Complete your accounting setup to optimize your tax savings and begin bookkeeping services.',
};

export const COMPLETE_PAYROLL_SETUP: LaunchpadTaskSchema = {
    id: 'complete_payroll_setup',
    dependency: ['open_connect_business_bank_account'],
    subtasks: [],
    title: 'Set up your payroll',
    description:
        'Paying yourself a salary through payroll is a requirement for operating as an S Corp. Make sure you set up and run payroll in the year that you become an S Corp.',
};

export const S_CORP_ELECTION: LaunchpadTaskSchema = {
    id: 's_corp_election',
    dependency: [],
    subtasks: [
        {
            id: S_CORP_ELECTION_UPLOAD_TASK,
            title: 'Complete your S Corp election form',
            description:
                'Print your pre-filled S Corp election form, sign, and send it back to us. The IRS requires that this form be signed in ink.',
        },
        {
            title: 'Collective is submitting your S Corp election forms',
            description:
                "Collective will review and submit your S Corp form within two business days. We'll reach out to you if we see any issues.",
            visual_dependencies: [
                OnboardingTaskTemplateName.REVIEW_SCORP_ELECTION_DOCS,
                OnboardingTaskTemplateName.FAX_SCORP_ELECTION_DOCS,
                OnboardingTaskTemplateName.CONFIRM_SCORP_ELECTION_DOCS_FAX,
            ],
        },
    ],
    title: 'Become an S Corp',
    description: "We've prepared your S Corp election documents. You just need to print, sign, and send it back to us.",
};

const INSURANCE_SETUP: LaunchpadTaskSchema = {
    id: INSURANCE_SETUP_ID,
    dependency: [],
    subtasks: [],
    title: 'Get your business insured',
    description: 'Explore low cost coverage to protect your business against common risks.',
};

/**
 * Returns the launchpad task schema based on user type and business address state.
 * The returned schema includes all possible tasks - filtering based on backend state
 * is handled by applySchemaValidators.
 */
export function getSchemaFromUserType(userType: UserType, businessAddressState?: string): LaunchpadTaskSchema[] {
    const shouldHavePublicationFlow = ['NY', 'KY', 'NE'].includes(businessAddressState || '') ?? false;
    const pubFlow = shouldHavePublicationFlow ? [getPublishBusinessSchema(businessAddressState).id] : [];
    const registerLLCSchema = getRegisterLLCSchema(businessAddressState);

    const schemas = {
        // Formation: SP -> LLC
        [UserType.SoleProprietor_LLC]: shouldHavePublicationFlow
            ? [
                  registerLLCSchema,
                  { ...INSURANCE_SETUP, dependency: [COMPLETE_APPLICATION_ID] },
                  getPublishBusinessSchema(businessAddressState),
                  { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [registerLLCSchema.id, ...pubFlow] },
                  COMPLETE_ACCOUNTING_SETUP,
              ]
            : [
                  registerLLCSchema,
                  { ...INSURANCE_SETUP, dependency: [COMPLETE_APPLICATION_ID] },
                  { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [registerLLCSchema.id] },
                  COMPLETE_ACCOUNTING_SETUP,
              ],

        // Formation: SP -> SCorp
        [UserType.SoleProprietor_SCorp]: shouldHavePublicationFlow
            ? [
                  registerLLCSchema,
                  { ...INSURANCE_SETUP, dependency: [COMPLETE_APPLICATION_ID] },
                  getPublishBusinessSchema(businessAddressState),
                  { ...S_CORP_ELECTION, dependency: [...pubFlow] },
                  { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [...pubFlow] },
                  {
                      ...COMPLETE_ACCOUNTING_SETUP,
                      dependency: [...COMPLETE_ACCOUNTING_SETUP.dependency, S_CORP_ELECTION.id],
                  },
                  {
                      ...COMPLETE_PAYROLL_SETUP,
                      dependency: [...COMPLETE_PAYROLL_SETUP.dependency, S_CORP_ELECTION.id],
                  },
              ]
            : [
                  registerLLCSchema,
                  { ...INSURANCE_SETUP, dependency: [COMPLETE_APPLICATION_ID] },
                  { ...S_CORP_ELECTION, dependency: [registerLLCSchema.id, ...pubFlow] },
                  { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [registerLLCSchema.id, ...pubFlow] },
                  {
                      ...COMPLETE_ACCOUNTING_SETUP,
                      dependency: [...COMPLETE_ACCOUNTING_SETUP.dependency, S_CORP_ELECTION.id],
                  },
                  {
                      ...COMPLETE_PAYROLL_SETUP,
                      dependency: [...COMPLETE_PAYROLL_SETUP.dependency, S_CORP_ELECTION.id],
                  },
              ],

        // Takeover: LLC -> SCorp
        [UserType.LLC_SCorp]: [
            BUSINESS_VERIFICATION_SCHEMA,
            { ...INSURANCE_SETUP, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            { ...S_CORP_ELECTION, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            {
                ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS,
                dependency: [BUSINESS_VERIFICATION_SCHEMA.id],
            },
            {
                ...COMPLETE_ACCOUNTING_SETUP,
                dependency: [...COMPLETE_ACCOUNTING_SETUP.dependency, S_CORP_ELECTION.id],
            },
            {
                ...COMPLETE_PAYROLL_SETUP,
                dependency: [...COMPLETE_PAYROLL_SETUP.dependency, S_CORP_ELECTION.id],
            },
        ],

        // Takeover: LLC -> LLC
        [UserType.LLC_LLC]: [
            BUSINESS_VERIFICATION_SCHEMA,
            { ...INSURANCE_SETUP, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            COMPLETE_ACCOUNTING_SETUP,
        ],

        // Takeover: SCorp -> SCorp
        [UserType.SCorp_SCorp]: [
            {
                ...BUSINESS_VERIFICATION_SCHEMA,
                subtasks: [
                    BUSINESS_VERIFICATION_UPLOAD_EIN_SUBTASK,
                    BUSINESS_VERIFICATION_UPLOAD_S_CORP_CONFIRMATION_LETTER_SUBTASK, // Insert S Corp confirmation
                    BUSINESS_VERIFICATION_MIDDESK_SUBTASK,
                    BUSINESS_VERIFICATION_VERIFY_EIN_CONFIRMATION_LETTER_SUBTASK,
                    VERIFY_SCORP_APPROVAL_LETTER_SUBTASK, // Add S Corp approval verification
                    BUSINESS_VERIFICATION_VERIFY_BUSINESS_SUBTASK,
                ],
            },
            { ...INSURANCE_SETUP, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            { ...OPEN_CONNECT_BUSINESS_BANK_ACCOUNTS, dependency: [BUSINESS_VERIFICATION_SCHEMA.id] },
            COMPLETE_ACCOUNTING_SETUP,
            COMPLETE_PAYROLL_SETUP,
        ],

        [UserType.SCorp_CCorp]: [],

        [UserType.SCorp_LLC]: [],
    };

    return schemas[userType] || [];
}

/**
 * Helper function to extract task names from a launchpad schema.
 * Extracts task IDs and subtask IDs/visual_dependencies for API calls.
 */
export function extractTaskNamesFromSchema(schema: LaunchpadTaskSchema[]): string[] {
    return schema.reduce<string[]>((pre, curr) => {
        if (curr.subtasks && curr.subtasks.length > 0) {
            const subtaskIds = curr.subtasks
                .flatMap((subtask) => {
                    if ('id' in subtask) {
                        return subtask.id;
                    }
                    if ('visual_dependencies' in subtask) {
                        return subtask.visual_dependencies;
                    }
                    return [];
                })
                .filter((id): id is string => typeof id === 'string');
            return [...pre, ...subtaskIds];
        }
        return [...pre, curr.id];
    }, []);
}
